import {
  ActionIcon,
  Badge,
  CloseButton,
  FileInput,
  Group,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconFile, IconPaperclip, IconSend } from '@tabler/icons-react'
import { useRef, useState } from 'react'
import { castArray, formatFileSize } from '../../../utils/src'
import { compressImage } from '../utils/image'
import { ChatHooks } from './hooks'

interface MessageInputProps {
  onSend: (content: string, attachmentIds?: number[]) => Promise<void> | void
  disabled?: boolean
  chatHooks: ChatHooks
}

interface PendingAttachment {
  file: File
  id: string
  attachmentId?: number
  progress: number
  error?: string
}

export function MessageInput({
  onSend,
  disabled,
  chatHooks,
}: MessageInputProps) {
  const isMobile = useMediaQuery('(pointer: coarse)')
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLButtonElement>(null)

  const createAttachment = chatHooks.useCreateAttachment()

  const form = useForm({
    initialValues: {
      content: '',
    },
    validate: {
      content: (value) => {
        if (
          !value.trim() &&
          !pendingAttachments.filter((a) => a.attachmentId).length
        ) {
          return 'Message cannot be empty'
        }
        return null
      },
    },
  })

  const uploadFile = async (attachment: PendingAttachment) => {
    try {
      // Compress image if it's an image file
      const fileToUpload = await compressImage(attachment.file)

      // Update progress
      setPendingAttachments((prev) =>
        prev.map((a) => (a.id === attachment.id ? { ...a, progress: 20 } : a)),
      )

      // Create attachment and get upload URL
      const {
        id: attachmentId,
        uploadUrl,
        fields,
      } = await createAttachment.mutateAsync({
        filename: fileToUpload.name,
        fileSize: fileToUpload.size,
        bucket: 'private',
        description: 'Chat attachment',
        entityType: 'chat_message',
      })

      // Update progress
      setPendingAttachments((prev) =>
        prev.map((a) => (a.id === attachment.id ? { ...a, progress: 50 } : a)),
      )

      // Upload to S3
      const formData = new FormData()
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string)
      })
      formData.append('file', fileToUpload)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      })

      if (!uploadResponse.ok) {
        throw new Error('Upload failed')
      }

      // Update attachment with ID and complete progress
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachment.id ? { ...a, attachmentId, progress: 100 } : a,
        ),
      )
    } catch (error) {
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachment.id
            ? {
                ...a,
                error: error instanceof Error ? error.message : 'Upload failed',
                progress: 0,
              }
            : a,
        ),
      )

      notifications.show({
        message: `Failed to upload ${attachment.file.name}`,
        color: 'red',
      })
    }
  }

  const handleSubmit = form.onSubmit(async (values) => {
    if (disabled) return

    // Get successfully uploaded attachment IDs
    const attachmentIds = pendingAttachments
      .filter((a) => a.attachmentId)
      .map((a) => a.attachmentId!)

    // Wait for message to be sent and cache updated
    await onSend(
      values.content.trim(),
      attachmentIds.length > 0 ? attachmentIds : undefined,
    )

    // Reset form and attachments
    form.reset()
    setPendingAttachments([])
  })

  const handleKeyPress = (event: React.KeyboardEvent) => {
    // On mobile (touch devices): Enter adds newline, user taps send button
    // On desktop: Enter sends message, Shift+Enter adds newline
    if (!isMobile && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleFileSelect = async (files: File | File[] | null) => {
    const fileArray = castArray(files)

    if (!fileArray.length) return

    const newAttachments: PendingAttachment[] = fileArray.map((file) => ({
      file,
      id: crypto.randomUUID(),
      progress: 0,
    }))

    setPendingAttachments((prev) => [...prev, ...newAttachments])
    setIsUploading(true)

    // Upload files in parallel
    await Promise.all(newAttachments.map(uploadFile))
    setIsUploading(false)
  }

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  return (
    <Stack gap="xs">
      {pendingAttachments.length > 0 && (
        <Paper p="xs" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Attachments
            </Text>
            {pendingAttachments.map((attachment) => (
              <div key={attachment.id}>
                <Group justify="space-between" gap="xs">
                  <Group gap="xs">
                    <IconFile size={16} />
                    <Text size="sm">{attachment.file.name}</Text>
                    <Badge size="sm" variant="outline">
                      {formatFileSize(attachment.file.size)}
                    </Badge>
                    {attachment.error && (
                      <Badge size="sm" color="red">
                        Failed
                      </Badge>
                    )}
                  </Group>
                  <CloseButton
                    size="sm"
                    onClick={() => removeAttachment(attachment.id)}
                    aria-label="Remove attachment"
                    disabled={
                      attachment.progress > 0 && attachment.progress < 100
                    }
                  />
                </Group>
                {attachment.progress > 0 && attachment.progress < 100 && (
                  <Progress value={attachment.progress} size="xs" mt={4} />
                )}
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      <form onSubmit={handleSubmit}>
        <Paper
          p="xs"
          radius="lg"
          withBorder
          style={{
            backgroundColor: 'var(--mantine-color-white)',
          }}
        >
          <Group gap="xs" wrap="nowrap">
            <FileInput
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={handleFileSelect}
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
            />

            <ActionIcon
              size="lg"
              variant="subtle"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading}
            >
              <IconPaperclip size={20} />
            </ActionIcon>

            <Textarea
              {...form.getInputProps('content')}
              placeholder="Kirjoita viesti..."
              style={{ flex: 1 }}
              onKeyDown={handleKeyPress}
              size="md"
              autoComplete="off"
              autosize
              radius={0}
              minRows={1}
              maxRows={4}
              variant="unstyled"
            />

            <ActionIcon
              size="lg"
              variant="filled"
              type="submit"
              disabled={
                disabled ||
                isUploading ||
                (!form.values.content.trim() &&
                  !pendingAttachments.filter((a) => a.attachmentId).length)
              }
            >
              {disabled ? (
                <Loader size={20} color="white" />
              ) : (
                <IconSend size={20} />
              )}
            </ActionIcon>
          </Group>
        </Paper>
      </form>
    </Stack>
  )
}
